import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import Header24 from './components/Header24';
import Footer24 from './components/Footer24';

const Signup24 = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        navigate('/template24/profile');
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-signup {
                    min-height: 100vh;
                    background: #FFFFFF;
                }
                .dhara-signup-split {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    min-height: calc(100vh - 80px);
                }
                .dhara-signup-image {
                    background-image: url('https://images.pexels.com/photos/6567340/pexels-photo-6567340.jpeg?auto=compress&cs=tinysrgb&w=1200');
                    background-size: cover;
                    background-position: center;
                    position: relative;
                }
                .dhara-signup-image::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: rgba(0, 85, 58, 0.06);
                }
                .dhara-signup-form-wrap {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 60px;
                }
                .dhara-signup-form {
                    width: 100%;
                    max-width: 440px;
                }
                .dhara-signup-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 40px;
                    font-weight: 400;
                    color: #1A1A1A;
                    margin-bottom: 12px;
                    letter-spacing: -0.5px;
                }
                .dhara-signup-subtitle {
                    font-family: 'Inter', sans-serif;
                    font-size: 15px;
                    color: #6B6B6B;
                    font-weight: 300;
                    margin-bottom: 40px;
                    line-height: 1.6;
                }
                .dhara-signup-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }
                .dhara-signup-field {
                    margin-bottom: 20px;
                }
                .dhara-signup-label {
                    display: block;
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 500;
                    letter-spacing: 0.5px;
                    color: #6B6B6B;
                    margin-bottom: 8px;
                }
                .dhara-signup-input-wrap {
                    position: relative;
                }
                .dhara-signup-input {
                    width: 100%;
                    padding: 14px 16px;
                    border: 1px solid #E5E5E5;
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #1A1A1A;
                    background: #FFFFFF;
                    outline: none;
                    transition: border-color 0.3s ease;
                    box-sizing: border-box;
                }
                .dhara-signup-input:focus {
                    border-color: #00553A;
                }
                .dhara-signup-input::placeholder {
                    color: #BDBDBD;
                }
                .dhara-signup-eye {
                    position: absolute;
                    right: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #6B6B6B;
                }
                .dhara-signup-submit {
                    width: 100%;
                    height: 52px;
                    background: #00553A;
                    color: #FFFFFF;
                    border: none;
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    font-weight: 500;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    cursor: pointer;
                    transition: background 0.3s ease;
                    margin-top: 12px;
                    margin-bottom: 24px;
                }
                .dhara-signup-submit:hover {
                    background: #006B4A;
                }
                .dhara-signup-login {
                    text-align: center;
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                }
                .dhara-signup-login-link {
                    color: #00553A;
                    cursor: pointer;
                    font-weight: 500;
                    background: none;
                    border: none;
                    padding: 0;
                    font-family: inherit;
                    font-size: inherit;
                    transition: opacity 0.3s ease;
                }
                .dhara-signup-login-link:hover {
                    opacity: 0.8;
                }
                .dhara-signup-error {
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    color: #C53030;
                    margin-bottom: 16px;
                    padding: 12px 16px;
                    background: #FFF5F5;
                    border: 1px solid #FED7D7;
                }

                @media (max-width: 768px) {
                    .dhara-signup-split {
                        grid-template-columns: 1fr;
                    }
                    .dhara-signup-image {
                        display: none;
                    }
                    .dhara-signup-form-wrap {
                        padding: 40px 20px;
                    }
                    .dhara-signup-title { font-size: 32px; }
                    .dhara-signup-row { grid-template-columns: 1fr; }
                }
            `}</style>

            <Header24 />

            <main className="dhara-signup">
                <div className="dhara-signup-split">
                    <div className="dhara-signup-image" />
                    <div className="dhara-signup-form-wrap">
                        <form className="dhara-signup-form" onSubmit={handleSubmit}>
                            <h1 className="dhara-signup-title">Join Dhara</h1>
                            <p className="dhara-signup-subtitle">
                                Create an account to explore our collections and track your orders.
                            </p>

                            {error && <div className="dhara-signup-error">{error}</div>}

                            <div className="dhara-signup-row">
                                <div className="dhara-signup-field">
                                    <label className="dhara-signup-label">First Name</label>
                                    <input
                                        className="dhara-signup-input"
                                        value={formData.firstName}
                                        onChange={(e) => handleChange('firstName', e.target.value)}
                                    />
                                </div>
                                <div className="dhara-signup-field">
                                    <label className="dhara-signup-label">Last Name</label>
                                    <input
                                        className="dhara-signup-input"
                                        value={formData.lastName}
                                        onChange={(e) => handleChange('lastName', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="dhara-signup-field">
                                <label className="dhara-signup-label">Email</label>
                                <input
                                    className="dhara-signup-input"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    placeholder="your@email.com"
                                />
                            </div>

                            <div className="dhara-signup-field">
                                <label className="dhara-signup-label">Phone</label>
                                <input
                                    className="dhara-signup-input"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    placeholder="+91"
                                />
                            </div>

                            <div className="dhara-signup-field">
                                <label className="dhara-signup-label">Password</label>
                                <div className="dhara-signup-input-wrap">
                                    <input
                                        className="dhara-signup-input"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={(e) => handleChange('password', e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="dhara-signup-eye"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                                    </button>
                                </div>
                            </div>

                            <div className="dhara-signup-field">
                                <label className="dhara-signup-label">Confirm Password</label>
                                <input
                                    className="dhara-signup-input"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                                />
                            </div>

                            <button type="submit" className="dhara-signup-submit">
                                Create Account
                            </button>

                            <div className="dhara-signup-login">
                                Already have an account?{' '}
                                <button
                                    type="button"
                                    className="dhara-signup-login-link"
                                    onClick={() => navigate('/template24/login')}
                                >
                                    Sign in
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </main>

            <Footer24 />
        </>
    );
};

export default Signup24;
